import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { azureBlobService, VideoMetadata } from '../services/azureBlobService';
import VideoPlayer from './VideoPlayer';
import '../styles/VideoList.css';

const VideoList: React.FC = () => {
  const { user } = useAuth();
  const [videos, setVideos] = useState<VideoMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedVideo, setSelectedVideo] = useState<VideoMetadata | null>(null);
  const [showPlayer, setShowPlayer] = useState(false);

  useEffect(() => {
    loadVideos();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadVideos = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError('');
      const allVideos = await azureBlobService.getVideosAsync();
      const userVideos = allVideos.filter((video: any) => video.uploadedBy === user.email);
      setVideos(userVideos);
    } catch (err) {
      console.error('Error loading videos:', err);
      setError('Failed to load videos. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVideoPlay = (video: VideoMetadata) => {
    setSelectedVideo(video);
    setShowPlayer(true);
  };

  const handleDeleteVideo = async (videoId: string) => {
    if (!window.confirm('Are you sure you want to delete this video?')) {
      return;
    }

    try {
      await azureBlobService.deleteVideo(videoId);
      await loadVideos(); // Refresh the list
    } catch (err) {
      console.error('Error deleting video:', err);
      setError('Failed to delete video. Please try again.');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (showPlayer && selectedVideo) {
    return (
      <VideoPlayer
        video={selectedVideo}
        onClose={() => {
          setShowPlayer(false);
          setSelectedVideo(null);
        }}
      />
    );
  }

  return (
    <div className="video-list-container">
      <div className="list-header">
        <h2>My Videos</h2>
        <p>Manage your uploaded video collection</p>
      </div>

      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your videos...</p>
        </div>
      )}

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
          <button onClick={loadVideos} className="retry-button">
            Retry
          </button>
        </div>
      )}

      {!loading && !error && videos.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📹</div>
          <h3>No videos uploaded yet</h3>
          <p>Start by uploading your first video to see it here.</p>
        </div>
      )}

      {!loading && !error && videos.length > 0 && (
        <>
          <div className="videos-stats">
            <div className="stat">
              <span className="stat-number">{videos.length}</span>
              <span className="stat-label">Videos</span>
            </div>
            <div className="stat">
              <span className="stat-number">
                {formatFileSize(videos.reduce((total, video) => total + video.size, 0))}
              </span>
              <span className="stat-label">Total Size</span>
            </div>
          </div>

          <div className="videos-grid">
            {videos.map((video) => (
              <div key={video.id} className="video-card">
                <div className="video-thumbnail">
                  <div className="thumbnail-placeholder">
                    <span className="play-icon">▶️</span>
                  </div>
                  <div className="video-duration">
                    {/* In a real app, you'd extract video duration */}
                    <span>--:--</span>
                  </div>
                </div>

                <div className="video-info">
                  <h3 className="video-title" title={video.name}>
                    {video.name}
                  </h3>
                  
                  <div className="video-metadata">
                    <span className="video-size">{formatFileSize(video.size)}</span>
                    <span className="video-date">{formatDate(video.uploadedAt)}</span>
                  </div>

                  <div className="video-actions">
                    <button
                      onClick={() => handleVideoPlay(video)}
                      className="action-button play-button"
                      title="Play video"
                    >
                      ▶️ Play
                    </button>
                    
                    <button
                      onClick={() => window.open(video.url, '_blank')}
                      className="action-button download-button"
                      title="Open in new tab"
                    >
                      🔗 Open
                    </button>
                    
                    <button
                      onClick={() => handleDeleteVideo(video.id)}
                      className="action-button delete-button"
                      title="Delete video"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default VideoList;